import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchWithAuth, isAuthenticated } from "../../auth/auth";
import { useAuthUser } from "../../utils/useAuthUser";
import { getApiUrl } from "../../utils/getApiUrl";
import { useI18n } from "../../utils/translate/i18n/useI18n";
import LoadingSpinner from "../../components/loadingSpinner/loadingSpinner";
import { BaseModal } from "../../components/modalCommon/modalBase";
import { ModalHeader } from "../../components/modalCommon/modalHeader";
import { ModalBody } from "../../components/modalCommon/modalBody";
import { ModalFooter } from "../../components/modalCommon/modalFooter";
import type { UserActivity } from "../../type/result";
import "./userActivity.scss";

type FetchState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "success"; data: UserActivity[] };

const KNOWN_ACTIONS = ["launch_app", "end_test", "download_result", "email_result"];
const TRUNCATE_THRESHOLD = 22;

function TruncatedCell({
  text, label, className, onExpand,
}: {
  text: string | null | undefined;
  label: string;
  className: string;
  onExpand: (label: string, value: string) => void;
}) {
  const { t } = useI18n();
  const str = text ?? null;
  const display = str ?? "—";
  const canExpand = str !== null && str.length > TRUNCATE_THRESHOLD;
  return (
    <td className={className} title={canExpand ? str : undefined}>
      {canExpand ? (
        <div className="user-activity__truncated">
          <span className="user-activity__truncated-text">{str}</span>
          <button
            type="button"
            className="user-activity__expand-btn"
            onClick={() => onExpand(label, str)}
            title={t("Pokaż całość")}
          >
            <i className="bi bi-arrows-angle-expand" />
          </button>
        </div>
      ) : display}
    </td>
  );
}

function parseSqlDate(dateStr: string): Date | null {
  const m = dateStr.match(
    /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/
  );
  if (!m) return null;
  const [, y, mo, d, h, mi, s = "0"] = m;
  return new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s));
}

function formatDate(dateStr: string, locale: string): string {
  const d = parseSqlDate(dateStr) ?? new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleString(locale, {
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

export default function UserActivity() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const user = useAuthUser();

  const [fetchState, setFetchState] = useState<FetchState>({ status: "loading" });
  const [expandedText, setExpandedText] = useState<{ label: string; value: string } | null>(null);

  // Filters panel
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Filters
  const [filterIp, setFilterIp] = useState("");
  const [filterAction, setFilterAction] = useState("");
  const [filterPage, setFilterPage] = useState("");
  const [filterDesc, setFilterDesc] = useState("");
  const [filterSession, setFilterSession] = useState("");
  const [displayLimit, setDisplayLimit] = useState<number | null>(200);

  // Applied filters (sent to server on fetch)
  const [appliedIp, setAppliedIp] = useState("");
  const [appliedAction, setAppliedAction] = useState("");
  const [appliedPage, setAppliedPage] = useState("");
  const [appliedDesc, setAppliedDesc] = useState("");
  const [appliedSession, setAppliedSession] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!isAuthenticated()) { navigate("/login"); return; }
    if (user && !user.admin) { navigate("/"); }
  }, [navigate, user]);

  useEffect(() => {
    if (!user?.admin) return;

    const params = new URLSearchParams();
    if (appliedIp)      params.set("ip_address",  appliedIp);
    if (appliedAction)  params.set("action",       appliedAction);
    if (appliedPage)    params.set("page",         appliedPage);
    if (appliedDesc)    params.set("description",  appliedDesc);
    if (appliedSession) params.set("session_id",   appliedSession);

    fetchWithAuth(`${getApiUrl("tools/activities")}?${params.toString()}`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<UserActivity[]>;
      })
      .then((data) => setFetchState({ status: "success", data }))
      .catch((err: unknown) =>
        setFetchState({ status: "error", message: err instanceof Error ? err.message : String(err) })
      );
  }, [user, appliedIp, appliedAction, appliedPage, appliedDesc, appliedSession, refreshKey]);

  const activities = useMemo(
    () => (fetchState.status === "success" ? fetchState.data : []),
    [fetchState]
  );

  const visibleActivities = useMemo(
    () => (displayLimit === null ? activities : activities.slice(0, displayLimit)),
    [activities, displayLimit]
  );

  // const distinctPages = useMemo(() => {
  //   const pages = new Set(activities.map((a) => a.page).filter(Boolean) as string[]);
  //   return Array.from(pages).sort();
  // }, [activities]);

  const sessionColorMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const a of activities) {
      if (a.session_id && !map.has(a.session_id)) {
        map.set(a.session_id, map.size % 2);
      }
    }
    return map;
  }, [activities]);

  const handleApply = () => {
    setFetchState({ status: "loading" });
    setRefreshKey((k) => k + 1);
    setAppliedIp(filterIp);
    setAppliedAction(filterAction);
    setAppliedPage(filterPage);
    setAppliedDesc(filterDesc);
    setAppliedSession(filterSession);
  };

  const handleReset = () => {
    setFetchState({ status: "loading" });
    setFilterIp(""); setFilterAction(""); setFilterPage(""); setFilterDesc(""); setFilterSession(""); setDisplayLimit(200);
    setRefreshKey((k) => k + 1);
    setAppliedIp(""); setAppliedAction(""); setAppliedPage(""); setAppliedDesc(""); setAppliedSession("");
  };

  const handleIpClick = (ip: string | null | undefined) => {
    if (!ip) return;
    setFiltersOpen(true);
    setFilterIp(ip);
    setFetchState({ status: "loading" });
    setRefreshKey((k) => k + 1);
    setAppliedIp(ip);
    setAppliedAction(filterAction);
    setAppliedPage(filterPage);
    setAppliedDesc(filterDesc);
    setAppliedSession(filterSession);
  };

  return (
    <div className="app-content__panel user-activity">

      {/* ── Filters ── */}
      <div className="card border-0 mb-3 user-activity__filters">
        <button
          className="user-activity__filters-toggle"
          onClick={() => setFiltersOpen((o) => !o)}
          aria-expanded={filtersOpen}
        >
          <span>{t("Filtry")}</span>
          <i className={`bi bi-chevron-down user-activity__filters-chevron${filtersOpen ? " user-activity__filters-chevron--open" : ""}`} />
        </button>
        {filtersOpen && (
        <div className="card-body p-3">
          <div className="row g-2 align-items-end">
            <div className="col-12 col-sm-6 col-md-3">
              <label className="form-label form-label--small">{t("Adres IP")}</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={filterIp}
                onChange={(e) => setFilterIp(e.target.value)}
                placeholder={t("Filtruj po IP...")}
                onKeyDown={(e) => e.key === "Enter" && handleApply()}
              />
            </div>
            <div className="col-12 col-sm-6 col-md-2">
              <label className="form-label form-label--small">{t("Akcja")}</label>
              <select
                className="form-select form-select-sm"
                value={filterAction}
                onChange={(e) => setFilterAction(e.target.value)}
              >
                <option value="">{t("Wszystkie")}</option>
                {KNOWN_ACTIONS.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </div>
            {/* <div className="col-12 col-sm-6 col-md-3">
              <label className="form-label form-label--small">{t("Strona")}</label>
              <select
                className="form-select form-select-sm"
                value={filterPage}
                onChange={(e) => setFilterPage(e.target.value)}
              >
                <option value="">{t("Wszystkie")}</option>
                {distinctPages.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div> */}
            <div className="col-12 col-sm-6 col-md-3">
              <label className="form-label form-label--small">{t("Opis")}</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={filterDesc}
                onChange={(e) => setFilterDesc(e.target.value)}
                placeholder={t("Filtruj po opisie...")}
                onKeyDown={(e) => e.key === "Enter" && handleApply()}
              />
            </div>
            {/* <div className="col-12 col-sm-6 col-md-3">
              <label className="form-label form-label--small">{t("Sesja")}</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={filterSession}
                onChange={(e) => setFilterSession(e.target.value)}
                placeholder={t("UUID sesji...")}
                onKeyDown={(e) => e.key === "Enter" && handleApply()}
              />
            </div> */}
            <div className="col-12 col-sm-6 col-md-2">
              <label className="form-label form-label--small">{t("Liczba wyników")}</label>
              <select
                className="form-select form-select-sm"
                value={displayLimit ?? ""}
                onChange={(e) => setDisplayLimit(e.target.value === "" ? null : Number(e.target.value))}
              >
                <option value="">{t("Wszystkie")}</option>
                <option value="200">200</option>
                <option value="500">500</option>
              </select>
            </div>
            <div className="col-12 col-md-1 d-flex gap-1">
              <button
                className="btn btn-primary btn-sm w-100"
                onClick={handleApply}
                title={t("Zastosuj filtry")}
              >
                <i className="bi bi-funnel-fill" />
              </button>
              <button
                className="btn btn-outline-secondary btn-sm w-100"
                onClick={handleReset}
                title={t("Wyczyść filtry")}
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>
          </div>
        </div>
        )}
      </div>

      {/* ── Content ── */}
      {fetchState.status === "loading" && <LoadingSpinner />}
      {fetchState.status === "error" && (
        <div className="alert alert-danger">{fetchState.message}</div>
      )}
      {fetchState.status === "success" && fetchState.data.length === 0 && (
        <div className="alert alert-info">{t("Brak wyników.")}</div>
      )}
      {fetchState.status === "success" && fetchState.data.length > 0 && (
        <div className="table-responsive user-activity__table-wrap">
          <table className="table table-sm table-hover user-activity__table">
            <thead>
              <tr>
                <th className="user-activity__col--narrow">{t("Data")}</th>
                <th className="user-activity__col--narrow">{t("IP")}</th>
                <th className="user-activity__col--narrow">{t("Akcja")}</th>
                {/* <th>{t("Strona")}</th> */}
                <th>{t("Opis")}</th>
                <th className="user-activity__col--narrow">{t("Sesja")}</th>
                <th className="user-activity__col--narrow">{t("Użytkownik")}</th>
                <th className="user-activity__col--narrow">{t("Wynik")}</th>
              </tr>
            </thead>
            <tbody>
              {fetchState.status === "success" && visibleActivities.map((a) => {
                const sessionColor = a.session_id != null ? sessionColorMap.get(a.session_id) : undefined;
                return (
                <tr
                  key={a.id}
                  className={sessionColor != null ? `user-activity__row--session-${sessionColor}` : undefined}
                >
                  <td className="user-activity__cell--date user-activity__cell--narrow">
                    {formatDate(a.created_at, locale)}
                  </td>
                  <td className="user-activity__cell--ip user-activity__cell--narrow">
                    <button
                      type="button"
                      className="user-activity__ip-btn"
                      onClick={() => handleIpClick(a.ip_address)}
                      title={t("Filtruj po tym IP")}
                    >
                      {a.ip_address}
                    </button>
                  </td>
                  <td className="user-activity__cell--narrow">
                    <span className={`badge user-activity__action-badge user-activity__action-badge--${a.action}`}>
                      {a.action}
                    </span>
                  </td>
                  {/* <TruncatedCell text={a.page} label={t("Strona")} className="user-activity__cell--page" onExpand={(label, value) => setExpandedText({ label, value })} /> */}
                  <TruncatedCell text={a.description} label={t("Opis")} className="user-activity__cell--desc" onExpand={(label, value) => setExpandedText({ label, value })} />
                  <td className="user-activity__cell--session user-activity__cell--narrow font-monospace" title={a.session_id ?? undefined}>
                    {a.session_id ? a.session_id.slice(0, 8) + "…" : "—"}
                  </td>
                  <TruncatedCell text={a.username ?? a.email ?? undefined} label={t("Użytkownik")} className="user-activity__cell--user" onExpand={(label, value) => setExpandedText({ label, value })} />
                  <td className="user-activity__cell--narrow">
                    {a.idtonalresult != null ? (() => {
                      const isVerbal = a.page === "/verbal-audiometry";
                      const paramKey = isVerbal ? "verbalResultId" : "resultId";
                      const icon = isVerbal ? "bi-chat-left-text" : "bi-bar-chart-line";
                      return (
                        <a
                          href="/user-results"
                          onClick={(e) => {
                            e.preventDefault();
                            const params = new URLSearchParams();
                            params.set(paramKey, String(a.idtonalresult));
                            if (a.iduser != null) params.set("userId", String(a.iduser));
                            navigate(`/user-results?${params.toString()}`);
                          }}
                          className="user-activity__result-link"
                          title={`#${a.idtonalresult}`}
                        >
                          <i className={`bi ${icon} me-1`} />
                          #{a.idtonalresult}
                        </a>
                      );
                    })() : (
                      "—"
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <BaseModal open={expandedText !== null} onClose={() => setExpandedText(null)} size="md">
        <ModalHeader title={expandedText?.label ?? ""} onClose={() => setExpandedText(null)} />
        <ModalBody>
          <p style={{ wordBreak: "break-all", whiteSpace: "pre-wrap", margin: 0 }}>
            {expandedText?.value}
          </p>
        </ModalBody>
        <ModalFooter>
          <button className="btn btn-secondary btn-sm" onClick={() => setExpandedText(null)}>
            {t("Zamknij")}
          </button>
        </ModalFooter>
      </BaseModal>
    </div>
  );
}
